import { cx } from 'emotion';
import React, { useCallback, useMemo, useEffect } from 'react';

import { DeviceInfo } from '@ezugi/utils';

import { PreloaderContext } from './components/PreloaderContext';
import { PreloaderFooter } from './components/PreloaderFooter';
import { PreloaderGameDetails } from './components/PreloaderGameDetails';
import { PreloaderHeader, PreloaderHeaderProps } from './components/PreloaderHeader';
import { PreloaderSpinner, PreloaderSpinnerProps } from './components/PreloaderSpinner';
import { containerClassName, defaultPreloaderClassName } from './style.css';

type PreloaderProps = {
  className?: string;
  fullScreenImage?: boolean;
  isLoading: boolean;
  show: boolean;
  isTapToPlayVisible: boolean;
  playLabel: string;
  onPlay?: () => void;
  onMount?: () => void;
  onDestroy: (e: TransitionEvent) => void;
  children: React.ReactNode;
  spinner?: React.FC<PreloaderSpinnerProps>;
}

function Preloader({
  children,
  className,
  isLoading,
  show,
  onPlay = () => {},
  onMount = () => {},
  onDestroy = () => {},
  fullScreenImage = false,
  playLabel,
  spinner,
  isTapToPlayVisible,
}: PreloaderProps) {
  const handleTapClick = useCallback(() => {
    if (DeviceInfo.isHandheld && !isLoading) {
      onPlay();
    }
  }, [ onPlay, isLoading ]);

  const showFooter = Boolean(!DeviceInfo.isHandheld && !fullScreenImage);

  const context = useMemo(
    () => ({
      isLoading,
      playLabel,
      showFooter,
      fullScreenImage,
      isTapToPlayVisible,
      spinner,
      onDestroy,
      show,
    }),
    [
      isLoading,
      playLabel,
      showFooter,
      fullScreenImage,
      isTapToPlayVisible,
      spinner,
      onDestroy,
      show,
    ]
  );

  useEffect(() => {
    onMount();
  }, [ onMount ]);

  return (
    <PreloaderContext.Provider value={context}>
      <div
        className={cx(
          containerClassName,
          defaultPreloaderClassName,
          className
        )}
        data-e2e="preloader"
        {...(isTapToPlayVisible && { onClick: handleTapClick })}
      >
        {children}
      </div>
    </PreloaderContext.Provider>
  );
}

Preloader.Spinner = PreloaderSpinner;
Preloader.Header = PreloaderHeader;
Preloader.Footer = PreloaderFooter;
Preloader.GameDetails = PreloaderGameDetails;

export {
  Preloader,
  type PreloaderHeaderProps,
  type PreloaderProps,
  type PreloaderSpinnerProps,
};
